import { PeriodRequest } from '@src/api/types/communityDashboard';
import { DATE_RANGE_PRESETS } from '@src/modules/constants/dateRangePreset';
import Button from '@src/ui/elements/button/Button';
import InputCalendar from '@src/ui/elements/form/InputCalendar';
import { firstError } from '@src/utils/errorMessageUtils';
import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import DateCalendar from './DateCalendar';

interface SummaryDateRangeSelectorProps {
  onChange: (range: {
    startDate: Date | null;
    endDate: Date | null;
    preset: PeriodRequest | null;
  }) => void;
  onClose?: () => void;
  initialStartDate: Date | null;
  initialEndDate: Date | null;
  initialPreset?: PeriodRequest;
  availableDateRange?: { earliest: Date | null; latest: Date | null };
  errorMessages?: string[];
}

function SummaryDateRangeSelector({
  onChange,
  onClose,
  initialStartDate,
  initialEndDate,
  initialPreset = '7',
  availableDateRange,
  errorMessages = [],
}: SummaryDateRangeSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<PeriodRequest>(initialPreset);

  const isValidDate = (date: Date | null): boolean => {
    return date instanceof Date && !isNaN(date.getTime());
  };
  const isApplyDisabled = useMemo(() => {
    const isValid = (d: Date | null) => d instanceof Date && !isNaN(d.getTime());
    return !(isValid(tempStartDate) && isValid(tempEndDate));
  }, [tempStartDate, tempEndDate]);

  const ALL_TIME_START_DATE = '2000-01-01';

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (initialPreset === 'custom') {
      setTempStartDate(initialStartDate);
      setTempEndDate(initialEndDate);
      setSelectedPreset('custom');
    } else if (initialPreset) {
      handlePresetSelect(initialPreset);
    }
  }, [initialStartDate, initialEndDate, initialPreset]);

  const handlePresetSelect = (preset: string) => {
    const today = dayjs().toDate();
    let start: Date;
    let end: Date = subDays(today, 1);

    switch (preset) {
      case '7':
        start = subDays(today, 7);
        break;
      case '30':
        start = subDays(today, 30);
        break;
      case '90':
        start = subDays(today, 90);
        break;
      case '365':
        start = subDays(today, 365);
        break;
      case 'all':
        if (availableDateRange?.earliest && availableDateRange?.latest) {
          start = availableDateRange.earliest;
          end = availableDateRange.latest;
        } else {
          start = dayjs(ALL_TIME_START_DATE).toDate();
        }
        break;

      case 'custom':
        setSelectedPreset('custom');
        return;
      default:
        return;
    }
    setTempStartDate(start);
    setTempEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (date: string | null, isUserInput = false) => {
    if (date && !isNaN(Date.parse(date))) {
      setTempStartDate(dayjs(date).toDate());
    } else {
      setTempStartDate(null);
    }

    if (isUserInput) {
      setSelectedPreset('custom');
    }
  };

  const handleEndDateChange = (date: string | null, isUserInput = false) => {
    if (date && !isNaN(Date.parse(date))) {
      setTempEndDate(dayjs(date).toDate());
    } else {
      setTempEndDate(null);
    }

    if (isUserInput) {
      setSelectedPreset('custom');
    }
  };

  const handleCalendarClick = (date: Date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date);
      setTempEndDate(null);
    } else {
      if (date < tempStartDate) {
        setTempEndDate(tempStartDate);
        setTempStartDate(date);
      } else {
        setTempEndDate(date);
      }
    }
    if (isValidDate(tempStartDate) && isValidDate(tempEndDate)) {
      setSelectedPreset('custom');
    }
  };

  const handleDateSubmit = () => {
    onChange({ startDate: tempStartDate, endDate: tempEndDate, preset: selectedPreset });
  };

  const handleOnClose = () => {
    onClose?.();
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const months = useMemo(() => {
    if (!availableDateRange?.earliest) {
      return [];
    }

    const start = dayjs(availableDateRange.earliest).startOf('month');
    const end = dayjs().startOf('month');
    const diffInMonths = end.diff(start, 'month') + 1;

    return Array.from({ length: diffInMonths }, (_, i) => {
      const date = start.add(i, 'month');
      return { year: date.year(), month: date.month() };
    });
  }, [availableDateRange?.earliest]);

  return (
    <div className='rounded-[4px] border border-gray-200 bg-white'>
      <div className='flex w-full'>
        <div className='flex w-[160px] border-r border-gray-200'>
          <ul>
            {DATE_RANGE_PRESETS.map((item) => (
              <li
                className='flex'
                key={item.key}
              >
                <a
                  className={`w-full cursor-pointer gap-2 whitespace-nowrap py-[9px] pl-4 pr-[3px] text-sm ${
                    selectedPreset === item.key ? 'bg-primary-500 text-white' : ''
                  }`}
                  onClick={() => handlePresetSelect(item.key)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className='flex-1'>
          <div className='flex items-center border-b border-gray-200 p-2'>
            <label className='relative text-xs'>
              <div className='absolute left-1/2 top-0 z-50 grid -translate-x-1/2 -translate-y-1/2 place-items-center bg-white p-px text-[10px] text-[#666666]'>
                開始日
              </div>
              <InputCalendar
                initialValue={tempStartDate ? tempStartDate.toISOString() : undefined}
                onChange={handleStartDateChange}
                className='size-full h-[37px] w-[107px] rounded-[4px] border border-gray-200 text-center'
                placeholderText='yyyy年MM月dd日'
                dateFormat={[
                  'yyyy年MM月dd日',
                  'yyyy-MM-dd',
                  'yyyy/MM/dd',
                  'yyyyMMdd',
                  'ddMMyyyy',
                  'dd/MM/yyyy',
                  'dd-MM-yyyy',
                ]}
              />
            </label>
            <div className='mx-1 text-[10px]'>ー</div>
            <label className='relative text-xs'>
              <div className='absolute left-1/2 top-0 z-50 grid -translate-x-1/2 -translate-y-1/2 place-items-center bg-white p-px text-[10px] text-[#666666]'>
                終了日
              </div>
              <InputCalendar
                initialValue={tempEndDate ? tempEndDate.toISOString() : undefined}
                onChange={handleEndDateChange}
                className='size-full h-[37px] w-[107px] rounded-[4px] border border-gray-200 text-center'
                placeholderText='yyyy年MM月dd日'
                dateFormat={[
                  'yyyy年MM月dd日',
                  'yyyy-MM-dd',
                  'yyyy/MM/dd',
                  'yyyyMMdd',
                  'ddMMyyyy',
                  'dd/MM/yyyy',
                  'dd-MM-yyyy',
                ]}
              />
            </label>
          </div>
          {!!errorMessages?.length && (
            <div className='flex items-center justify-center border-b border-gray-200 p-2'>
              <div
                className='text-xs text-warning'
                dangerouslySetInnerHTML={{ __html: firstError(errorMessages) }}
              />
            </div>
          )}

          <div
            ref={scrollRef}
            className='mt-2 flex h-[360px] flex-col gap-2.5 overflow-y-auto pb-4 pl-4'
          >
            {months.map(({ year, month }) => (
              <DateCalendar
                key={`${year}-${month}`}
                year={year}
                month={month}
                startDate={tempStartDate}
                endDate={tempEndDate}
                onDateClick={handleCalendarClick}
              />
            ))}
          </div>
        </div>
      </div>

      <div className='flex items-center justify-between border-t border-gray-200 px-8 py-2'>
        <div className='w-[160px]'>
          <Button
            size='s'
            variant='secondary'
            fullRounded
            fullWidth
            outline
            onClick={handleOnClose}
          >
            キャンセル
          </Button>
        </div>
        <div className='w-[160px]'>
          <Button
            size='s'
            variant='primary'
            fullRounded
            fullWidth
            outline
            onClick={handleDateSubmit}
            disabled={isApplyDisabled}
          >
            適用する
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SummaryDateRangeSelector;
